import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import {
  Button,
  Stack,
  TextInput,
  Text,
  Textarea,
  LoadingOverlay,
  Center,
  Alert,
} from "@mantine/core";
import { IBoundingBox, IPoint, Scanner } from "@yudiel/react-qr-scanner";
import { Cubby } from "../../stores/Cubby";
import AdvancedSettings from "./../advancedSendLightningSettings";
import {
  DEFAULT_PATH_FIND_TIMEOUT_IN_SECONDS,
  MODALS,
} from "../../helpers/constants";
import {
  handleInvoiceChange,
  handlePaymentAmountChange,
  handleMaxFeeChange,
  handlePathFindingTimeoutChange,
  isTwoFaSatisfied,
  isPayButtonDisabled,
} from "../../helpers/sendInvoiceHelpers";
import { useMediaQuery } from "@mantine/hooks";
import { IconAt, IconInfoCircle, IconScan } from "@tabler/icons-react";

export interface IDetectedBarcode {
  boundingBox: IBoundingBox;
  cornerPoints: IPoint[];
  format: string;
  rawValue: string;
}

const SendViaBolt11Invoice = observer(({ cubby }: { cubby: Cubby }) => {
  const [error, setError] = useState<string | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<string>("0");
  const [paymentAmountError, setPaymentAmountError] = useState<string | null>(
    null
  );
  const [btcAmount, setBtcAmount] = useState<string>("0");
  const [outgoingChannelScid, setOutgoingChannelScid] = useState<string | null>(
    null
  );
  const [maxFee, setMaxFee] = useState<string | null>(null);
  const [pathFindingTimeout, setPathFindingTimeout] = useState<string | null>(
    DEFAULT_PATH_FIND_TIMEOUT_IN_SECONDS.toString()
  );
  const [maxFeeError, setMaxFeeError] = useState<string | null>(null);
  const [pathFindingTimeoutError, setPathFindingTimeoutError] = useState<
    string | null
  >(null);
  const [twoFACode, setTwoFACode] = useState("");
  const [advancedSettingsOpened, setAdvancedSettingsOpened] = useState(false);
  const [isPaymentInProgress, setIsPaymentInProgress] = useState(false);
  const [loadingOverlayVisible, setLoadingOverlayVisible] = useState(false);
  const [invoiceDescription, setInvoiceDescription] = useState<string | null>(
    null
  );
  const [mobileScannerActive, setMobileScannerActive] = useState(false);
  const [twoFAError, setTwoFAError] = useState<string | null>(null);
  const isMobile = useMediaQuery("(max-width: 768px)");

  useEffect(() => {
    cubby.cubbyFastInfo("does_need_2FA_form");
  }, []);

  useEffect(() => {
    if (cubby.lastDecodedPayRequest?.data?.description) {
      setInvoiceDescription(cubby.lastDecodedPayRequest.data.description);
    }
  }, [cubby.lastDecodedPayRequest?.data?.description]);

  const handlePayInvoice = () => {
    let amountToPay =
      paymentAmount || cubby.lastDecodedPayRequest.data.safe_tokens;

    // Ensure amountToPay is never 0 or "0"
    if (amountToPay === 0 || amountToPay === "0") {
      amountToPay = null;
    }

    const maxFeeValue = maxFee ? parseInt(maxFee, 10) : null;
    const pathFindingTimeoutValue = pathFindingTimeout
      ? parseInt(pathFindingTimeout, 10)
      : null;

    const dataHash = {
      amountInSatoshis: amountToPay,
      outgoingChannelScid,
      maxFee: maxFeeValue,
      pathFindingTimeout: pathFindingTimeoutValue,
      payRequest: cubby.lastProposedLightningInvoiceForPayment,
      twoFAcode: twoFACode,
    };

    console.log("paying invoice with this data", dataHash);
    setIsPaymentInProgress(true);
    setTimeout(() => {
      cubby.cubbyLife("dangerous_pay_invoice", dataHash);
      setIsPaymentInProgress(false);
      cubby.clearLightningInvoiceStringForPayment();
      cubby.setModal(null);
    }, 3000);
  };

  const handleScannerToggle = () => {
    setMobileScannerActive((prev) => !prev);
  };

  const handleScan = (detectedCodes: IDetectedBarcode[]) => {
    if (detectedCodes.length > 0 && detectedCodes[0].rawValue) {
      const detectedBarcode = detectedCodes[0];
      handleInvoiceChange(
        detectedBarcode.rawValue,
        cubby,
        setError,
        setLoadingOverlayVisible
      );
      setMobileScannerActive(false);
    }
  };

  const renderPaymentInterface = () => {
    const { safe_tokens, expires_at } = cubby.lastDecodedPayRequest.data;
    const expiresInSeconds =
      (new Date(expires_at).getTime() - new Date().getTime()) / 1000;

    if (expiresInSeconds <= 60) {
      return (
        <Text>
          This invoice has expired. Please request a new invoice from your
          recipient.
        </Text>
      );
    }

    const amountToPay = paymentAmount || safe_tokens;
    const possibleChannels = cubby.outBoundChannelChoices(amountToPay);

    if (amountToPay >= cubby.outboundLiquidity && paymentAmount === "") {
      return (
        <Text>
          Error: Invoice amount exceeds outbound liquidity. Open another channel
          to pay this invoice.
        </Text>
      );
    }

    return (
      <>
        {safe_tokens > 0 && (
          <Text size="sm" ta="center">
            {safe_tokens.toLocaleString()} satoshis
          </Text>
        )}
        {invoiceDescription && (
          <Text size="sm" ta="center">
            "{invoiceDescription}"
          </Text>
        )}
        {safe_tokens === 0 && (
          <TextInput
            label="Payment Amount (satoshis)"
            description={paymentAmount ? `${btcAmount} BTC` : undefined}
            placeholder="Enter payment amount"
            value={paymentAmount}
            onChange={(e) =>
              handlePaymentAmountChange(
                e,
                cubby,
                setPaymentAmountError,
                setPaymentAmount,
                setBtcAmount
              )
            }
            error={paymentAmountError}
          />
        )}

        {cubby.shouldShow2FAFormForPayments && (
          <TextInput
            label="Enter 2FA Code"
            maxLength={7}
            placeholder="Get the code from your authenticator app"
            value={twoFACode}
            onChange={(event) => {
              const sanitizedValue = event.currentTarget.value
                .replace(/\s+/g, "")
                .replace(/\D/g, "");
              setTwoFACode(sanitizedValue);

              if (sanitizedValue.length > 0 && sanitizedValue.length !== 6) {
                setTwoFAError("A 2FA Code is six digits");
              } else {
                setTwoFAError(null);
              }
            }}
            error={twoFAError}
            mt="md"
          />
        )}

        <AdvancedSettings
          advancedSettingsOpened={advancedSettingsOpened}
          setAdvancedSettingsOpened={setAdvancedSettingsOpened}
          possibleChannels={possibleChannels}
          outgoingChannelScid={outgoingChannelScid}
          handleOutgoingChannelChange={setOutgoingChannelScid}
          maxFee={maxFee}
          handleMaxFeeChange={(e) =>
            handleMaxFeeChange(
              e,
              cubby,
              paymentAmount,
              setMaxFeeError,
              setMaxFee
            )
          }
          maxFeeError={maxFeeError}
          pathFindingTimeout={pathFindingTimeout}
          handlePathFindingTimeoutChange={(e) =>
            handlePathFindingTimeoutChange(
              e,
              setPathFindingTimeoutError,
              setPathFindingTimeout
            )
          }
          pathFindingTimeoutError={pathFindingTimeoutError}
        />

        <Button
          onClick={handlePayInvoice}
          loading={isPaymentInProgress}
          disabled={isPayButtonDisabled(
            cubby.shouldShow2FAFormForPayments,
            twoFACode,
            twoFAError,
            maxFeeError,
            error,
            paymentAmountError,
            safe_tokens,
            paymentAmount
          )}
        >
          Pay Invoice
        </Button>
      </>
    );
  };

  return (
    <Stack>
      <div style={{ position: "relative" }}>
        <LoadingOverlay
          visible={loadingOverlayVisible}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 1 }}
        />
        <Textarea
          placeholder="Paste a Lightning invoice here"
          value={cubby.lastProposedLightningInvoiceForPayment}
          onChange={(e) =>
            handleInvoiceChange(
              e.currentTarget.value,
              cubby,
              setError,
              setLoadingOverlayVisible
            )
          }
          error={error}
          minRows={4}
          maxRows={6}
          autosize
        />

        {cubby.lastProposedLightningInvoiceForPayment.includes("@") && (
          <Stack>
            <Alert
              variant="light"
              color="blue"
              title="Are you trying to pay to a lightning address?"
              icon={<IconInfoCircle />}
            >
              What you've entered looks like a Lightning address.
            </Alert>
            <Button
              leftSection={<IconAt />}
              size="sm"
              variant="default"
              aria-label="pay to lightning address"
              onClick={() => {
                setIsPaymentInProgress(false);
                cubby.clearLightningInvoiceStringForPayment();
                cubby.setModal(MODALS.PAY_TO_LIGHTNING_ADDRESS);
              }}
            >
              Pay To Lightning Address
            </Button>
          </Stack>
        )}

        {cubby.lastProposedLightningInvoiceForPayment == "" && (
          <Center>
            <Stack>
              <Button
                onClick={handleScannerToggle}
                leftSection={<IconScan size={18} />}
                mt="md"
              >
                {mobileScannerActive ? "Close Scanner" : "Scan QR Code"}
              </Button>
              {!isMobile && (
                <Text size="sm" c="dimmed">
                  QR Scanning doesn't always work on laptops and desktops.
                  You'll need to give your browser camera permissions. Or try
                  pasting an invoice instead.
                </Text>
              )}
            </Stack>
          </Center>
        )}
      </div>

      {mobileScannerActive && <Scanner onScan={handleScan} />}
      {cubby.lastDecodedPayRequest && renderPaymentInterface()}
    </Stack>
  );
});

export default SendViaBolt11Invoice;
